import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  HOMECARD_IMAGE_RATIOS,
  CloseButton,
  CHANNELS
} from '@spotahome/ui-library';
import { useSoyuzLocales } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { Mediator } from '@spotahome/soyuz-mediator';

import { useFiltersContext } from '@spotahome/ui-library/src/tenant/context/FiltersContext';
import { HomecardFormatter } from '@spotahome/ui-library/src/tenant/utils/images';
import { getDateAsUTC } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { useHighlightMarkersContext } from '../../context/HighlightMarkersContext';

import Homecard from '../Homecard';

import getGqlClient from '../../graphql/client';
import { getHomecardMapQuery } from '../../graphql/queries/homecards';
import { getMonthlyPrice } from '../../utils/homecards';
import useHomecardListTracking from '../../utils/homecards-tracking';

import HomeCardPlaceHolder from '../Homecard/HomeCardPlaceHolder';

import './HomecardMap.scss';

const fetchHomecard = async (id, locale) => {
  const { data } = await getHomecardMapQuery(getGqlClient())([id], locale);
  return (data && data.homecards && data.homecards[0]) || undefined;
};

const handleHomecardImageChange = (action, { index }) => {
  SoyuzAnalytics.sendGA4Event(`homecard-image-slide`, { action, index });
};

const HomecardMap = ({ cityId, deviceType, context = 'search' }) => {
  const [loading, setLoading] = useState(false);
  const [homecard, setHomecard] = useState();
  const { filters } = useFiltersContext();
  const { current: locale } = useSoyuzLocales();
  const moveIn = filters['move-in'];
  const moveOut = filters['move-out'];
  const { trackHomecardClick } = useHomecardListTracking(cityId);
  const { removeSelectedMarker, markerClickedId: homecardId } =
    useHighlightMarkersContext();

  const currentYear = getDateAsUTC(new Date()).getFullYear();

  const isSearchWithoutMap = context === 'search-without-map';

  const firstAvailableDate =
    homecard && getDateAsUTC(homecard.firstAvailableDate);
  const firstAvailableYear = firstAvailableDate?.getFullYear();
  const shouldShowAvailableFrom = !filters['move-in'];
  const baseClassName = `homecard-map homecard-map--${context}`;
  const triggerHomecardOpen = () =>
    Mediator.publish(CHANNELS.HOMECARD_MAP_OPEN);

  useEffect(() => {
    if (homecardId) {
      setLoading(true);
      triggerHomecardOpen();
      fetchHomecard(homecardId, locale)
        .then(result => {
          setHomecard({
            ...result,
            monthlyPrice: getMonthlyPrice(result.displayPrice)
          });
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          Mediator.publish(CHANNELS.HOMECARD_MAP_CLOSE);
        });
    } else {
      Mediator.publish(CHANNELS.HOMECARD_MAP_CLOSE);
    }
  }, [homecardId, locale]);

  useEffect(() => {
    if (homecard) {
      triggerHomecardOpen();
    }
    return () => {
      // hide component from outside
      Mediator.publish(CHANNELS.HOMECARD_MAP_CLOSE);
    };
  }, []);

  if (!homecardId || !homecard) {
    return null;
  }

  if (loading || !homecard) {
    return (
      <div className={baseClassName}>
        <HomeCardPlaceHolder />
      </div>
    );
  }

  const handleCloseMarker = e => {
    e.preventDefault();
    e.stopPropagation();
    removeSelectedMarker();
    Mediator.publish(CHANNELS.HOMECARD_MAP_CLOSE);
  };

  const homecardData = {
    ...homecard,
    photos: HomecardFormatter.getPhotos(
      homecard.photoIds,
      homecard.otherPropertyPhotoIds
    ),
    monthlyPrice: getMonthlyPrice(homecard.displayPrice)
  };

  const isDesktop = !(deviceType === 'smartphone' || deviceType === 'tablet');

  return (
    <div className={baseClassName}>
      {!isSearchWithoutMap && (
        <CloseButton
          className="homecard-map__close"
          size="small"
          onClick={handleCloseMarker}
        />
      )}
      <Homecard
        isShowStatus
        data={homecardData}
        moveIn={moveIn}
        moveOut={moveOut}
        flexibleMoveOut={!!filters.flexibleMoveOut}
        flexibleMoveIn={!!filters.flexibleMoveIn}
        onClick={() => {
          trackHomecardClick(0, homecardData);
        }}
        reviews={homecardData.reviews}
        hasLazyLoad={false}
        showPrice
        newTab={isDesktop}
        showAvailableFromYear={
          shouldShowAvailableFrom && firstAvailableYear !== currentYear
        }
        pageType="search"
        onImageChange={handleHomecardImageChange}
        isBooked={homecard.so}
        showSquaredMeters
        imageRatio={
          isDesktop
            ? HOMECARD_IMAGE_RATIOS.HOMECARD
            : HOMECARD_IMAGE_RATIOS.PANORAMIC
        }
        isEmbedded
        dataTest="horizontal-homecard"
        imageClassName={isSearchWithoutMap ? 'homecard-map__image' : ''}
        contentClassName={isSearchWithoutMap ? 'homecard-map__content' : ''}
        renderRightFloatingElements={() =>
          isSearchWithoutMap ? (
            <CloseButton onClick={handleCloseMarker} circled />
          ) : null
        }
      />
    </div>
  );
};

HomecardMap.propTypes = {
  cityId: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
  context: PropTypes.oneOf(['search', 'seo'])
};

export default HomecardMap;
